/**
 * Blender-style number fields, for every `<input type="number">` in the app
 * (React and plain DOM alike):
 *  - a click edits the value as before (focused, all selected);
 *  - pressing and dragging UP or DOWN anywhere on the field scrubs the value
 *    (up = more; vertical leaves room to travel in narrow panels) with a
 *    mouse-like acceleration curve: every pixel is worth more the faster the
 *    hand moves, smoothly (no modes, no jumps) — a crawl moves in hundredths
 *    of the field's unit, a flick covers big ranges; Shift = fine (×0.1),
 *    Ctrl = coarse (×10); Escape puts the value back;
 *  - fields drawn with step arrows (see NumberStepArrows / attachNumberArrows)
 *    step on a click of ‹ / ›.
 *
 * Values are written the way typing writes them (native setter + `input`
 * event), and a scrub or arrow click ends with `change` + blur, so React
 * fields that commit on blur, undo transactions and plain-DOM `change`
 * listeners all see one ordinary edit.
 */

/** A press that moves less than this (px) is a click. */
const DRAG_THRESHOLD_PX = 3;
/**
 * The acceleration curve, in field units (the step) per pixel:
 * `gain(v) = MIN_GAIN · (1 + (v / CURVE_SPEED)^CURVE_POWER)`, capped at
 * MAX_GAIN — at a crawl ~100 px per unit, ~12 px at a normal pace, a few
 * units per pixel when flicked: steep in speed (the hand decides), smooth in
 * between.
 */
const MIN_GAIN = 0.01;
const MAX_GAIN = 3;
const CURVE_SPEED = 0.2;
const CURVE_POWER = 2.2;
/** How much of the newest speed sample counts (the rest is the running average): smooths hand jitter. */
const SPEED_SMOOTHING = 0.35;
/** The finest a field without its own step shows: a hundredth of its unit, at most 3 decimals. */
const FREE_RESOLUTION = 0.01;
const MIN_RESOLUTION = 0.001;

export interface ScrubModifiers {
  /** Shift: ×0.1. */
  fine?: boolean;
  /** Ctrl / Cmd: ×10. */
  coarse?: boolean;
}

/** The field's step: its `step` attribute, else inferred from the value's decimals (whole numbers step by 1). */
export function inputStep(input: Pick<HTMLInputElement, 'step' | 'value'>): number {
  const step = Number(input.step);
  if (input.step && input.step !== 'any' && Number.isFinite(step) && step > 0) return step;
  const decimals = input.value.includes('.') ? Math.min(3, input.value.split('.')[1].length) : 0;
  return decimals > 0 ? 10 ** -decimals : 1;
}

/** Decimals a step shows (0.01 → 2, 5 → 0). */
export function stepDecimals(step: number): number {
  if (step >= 1) return 0;
  return Math.min(6, Math.max(0, Math.ceil(-Math.log10(step) - 1e-9)));
}

/** Units per pixel at a (smoothed) hand speed in px/ms: the acceleration curve. */
export function scrubGain(speed: number): number {
  return Math.min(MAX_GAIN, MIN_GAIN * (1 + Math.pow(Math.max(0, speed) / CURVE_SPEED, CURVE_POWER)));
}

/** How much `dx` px at `speed` px/ms changes the value, in the field's own unit (`step`). */
export function scrubDelta(dx: number, speed: number, step: number, modifiers: ScrubModifiers = {}): number {
  const factor = modifiers.fine ? 0.1 : modifiers.coarse ? 10 : 1;
  return dx * scrubGain(speed) * step * factor;
}

/** Whether the field states its own step (then values stay on it); otherwise it moves finely. */
export function hasOwnStep(input: Pick<HTMLInputElement, 'step'>): boolean {
  const step = Number(input.step);
  return !!input.step && input.step !== 'any' && Number.isFinite(step) && step > 0;
}

/** The grid a scrubbed value snaps to: the field's own step, else a hundredth of its unit (≥ 0.001). */
export function scrubResolution(input: Pick<HTMLInputElement, 'step' | 'value'>, fine: boolean): number {
  const step = inputStep(input);
  if (hasOwnStep(input)) return fine ? step / 10 : step;
  return Math.max(MIN_RESOLUTION, step * FREE_RESOLUTION);
}

/** A raw (accumulated) value as the field shows it: clamped to min/max and rounded to the step. */
export function snapToField(value: number, step: number, min?: number, max?: number, fine = false): number {
  // Shift works below the step (a tenth of it).
  const grid = fine ? step / 10 : step;
  let v = Math.round(value / grid) * grid;
  if (min !== undefined && Number.isFinite(min)) v = Math.max(min, v);
  if (max !== undefined && Number.isFinite(max)) v = Math.min(max, v);
  return Number(v.toFixed(stepDecimals(grid)));
}

const bound = (attr: string): number | undefined => (attr === '' ? undefined : Number(attr));

/** Write a value like typing does: React's tracked setter + `input` (React onChange and DOM listeners fire). */
export function writeNumberInput(input: HTMLInputElement, value: number): void {
  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
  if (setter) setter.call(input, String(value));
  else input.value = String(value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Finish an edit made from outside the keyboard: `change`, then blur (fields that commit on blur commit). */
function finishEdit(input: HTMLInputElement): void {
  input.dispatchEvent(new Event('change', { bubbles: true }));
  input.blur();
  input.classList.remove(STEPPING_CLASS);
  thawLook(input);
}

/** What a focused field changes about its look (focus ring, border, the fade into it). */
const FROZEN_LOOK = ['transition', 'border-color', 'box-shadow', 'outline', 'background-color'] as const;

/** The field's own inline values of FROZEN_LOOK while frozen (put back on thaw). */
const inlineBeforeFreeze = new WeakMap<HTMLInputElement, Array<[string, string, string]>>();

/** Pins the field's current (idle) look inline, so the focus an arrow step or a drag needs doesn't show. */
function freezeLook(input: HTMLInputElement): void {
  if (inlineBeforeFreeze.has(input)) return;
  inlineBeforeFreeze.set(
    input,
    FROZEN_LOOK.map((property) => [property, input.style.getPropertyValue(property), input.style.getPropertyPriority(property)]),
  );
  const idle = getComputedStyle(input);
  for (const property of FROZEN_LOOK) input.style.setProperty(property, property === 'transition' ? 'none' : idle.getPropertyValue(property), 'important');
}

function thawLook(input: HTMLInputElement): void {
  for (const [property, value, priority] of inlineBeforeFreeze.get(input) ?? []) {
    if (value) input.style.setProperty(property, value, priority);
    else input.style.removeProperty(property);
  }
  inlineBeforeFreeze.delete(input);
}

/** On a field while an arrow step is in flight: it keeps its idle look (centered, arrows shown) instead of flashing the edit state. */
const STEPPING_CLASS = 'number-stepping';

/** One step down (−1) or up (+1), as the ‹ › arrows do; Shift / Ctrl scale it like a drag. */
export function stepNumberInput(input: HTMLInputElement, direction: 1 | -1, modifiers: ScrubModifiers = {}): void {
  if (input.disabled || input.readOnly) return;
  const step = inputStep(input);
  const current = Number(input.value);
  const factor = modifiers.fine ? 0.1 : modifiers.coarse ? 10 : 1;
  const next = snapToField((Number.isFinite(current) ? current : (bound(input.min) ?? 0)) + direction * step * factor, step, bound(input.min), bound(input.max), modifiers.fine);
  // Focused like a typed edit, so edit transactions (undo) frame it.
  freezeLook(input);
  input.classList.add(STEPPING_CLASS);
  input.focus({ preventScroll: true });
  writeNumberInput(input, next);
  // Finished a tick later: focus, the new value and the blur in one go would reach a field that commits its draft on
  // blur (the properties panel's) before it re-rendered with that value — it would commit its previous draft instead,
  // undoing the step.
  setTimeout(() => finishEdit(input), 0);
}

const isScrubbable = (el: EventTarget | null): el is HTMLInputElement => el instanceof HTMLInputElement && el.type === 'number' && !el.disabled && !el.readOnly && el.dataset['noScrub'] === undefined;

/**
 * Install the drag / click behaviour on the whole document (once, at app
 * start). A field already being edited behaves natively (drag selects text).
 * Returns the uninstall.
 */
export function installNumberInputScrub(doc: Document = document): () => void {
  let state: { input: HTMLInputElement; pointerId: number; startY: number; lastY: number; lastT: number; start: number; raw: number; dragging: boolean; speed: number; unit: number; resolution: number } | null = null;

  const end = (commit: boolean): void => {
    if (!state) return;
    const { input, dragging, start, pointerId } = state;
    state = null;
    doc.body.classList.remove('number-scrubbing');
    input.classList.remove('number-scrubbing');
    if (input.hasPointerCapture?.(pointerId)) input.releasePointerCapture(pointerId);
    if (!dragging) {
      // A click: edit it, all selected (as Blender does).
      input.focus({ preventScroll: true });
      input.select();
      return;
    }
    if (!commit) writeNumberInput(input, start);
    finishEdit(input);
  };

  const onPointerDown = (e: PointerEvent): void => {
    const input = e.target;
    if (e.button !== 0 || !isScrubbable(input) || doc.activeElement === input) return;
    // Not focused yet: hold the focus back until we know it's a click, not a drag.
    e.preventDefault();
    const value = Number(input.value);
    const start = Number.isFinite(value) && input.value !== '' ? value : (bound(input.min) ?? 0);
    // The unit and resolution are fixed for the drag (the shown decimals change while scrubbing).
    state = { input, pointerId: e.pointerId, startY: e.clientY, lastY: e.clientY, lastT: e.timeStamp, start, raw: start, dragging: false, speed: 0, unit: inputStep(input), resolution: scrubResolution(input, false) };
    input.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (!state || e.pointerId !== state.pointerId) return;
    const { input } = state;
    if (!state.dragging) {
      if (Math.abs(e.clientY - state.startY) < DRAG_THRESHOLD_PX) return;
      state.dragging = true;
      state.lastY = state.startY;
      // Focused while scrubbing: fields frame the drag as one edit (undo), without a visible caret or focus look.
      freezeLook(input);
      input.focus({ preventScroll: true });
      input.classList.add('number-scrubbing');
      doc.body.classList.add('number-scrubbing');
    }
    // Up (screen y decreasing) is more.
    const delta = state.lastY - e.clientY;
    const dt = e.timeStamp - state.lastT;
    state.lastY = e.clientY;
    state.lastT = e.timeStamp;
    if (delta === 0) return;
    // A running average of the hand's speed: the gain follows it smoothly.
    // Events with no time between them (coalesced) count as one millisecond apart: a quick move.
    const sample = Math.abs(delta) / Math.max(1, dt);
    state.speed = state.speed + SPEED_SMOOTHING * (sample - state.speed);
    state.raw += scrubDelta(delta, state.speed, state.unit, { fine: e.shiftKey, coarse: e.ctrlKey || e.metaKey });
    const min = bound(input.min);
    const max = bound(input.max);
    // The accumulator stays within the bounds, so turning back responds at once.
    if (min !== undefined) state.raw = Math.max(min, state.raw);
    if (max !== undefined) state.raw = Math.min(max, state.raw);
    const next = snapToField(state.raw, hasOwnStep(input) && e.shiftKey ? state.resolution / 10 : state.resolution, min, max);
    if (String(next) !== input.value) writeNumberInput(input, next);
  };

  const onPointerUp = (e: PointerEvent): void => {
    if (state && e.pointerId === state.pointerId) end(true);
  };
  const onPointerCancel = (e: PointerEvent): void => {
    if (state && e.pointerId === state.pointerId) end(false);
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (state?.dragging && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      end(false);
    }
  };

  doc.addEventListener('pointerdown', onPointerDown, true);
  doc.addEventListener('pointermove', onPointerMove, true);
  doc.addEventListener('pointerup', onPointerUp, true);
  doc.addEventListener('pointercancel', onPointerCancel, true);
  doc.addEventListener('keydown', onKeyDown, true);
  return () => {
    doc.removeEventListener('pointerdown', onPointerDown, true);
    doc.removeEventListener('pointermove', onPointerMove, true);
    doc.removeEventListener('pointerup', onPointerUp, true);
    doc.removeEventListener('pointercancel', onPointerCancel, true);
    doc.removeEventListener('keydown', onKeyDown, true);
  };
}

const ARROW_LEFT_SVG = '<svg viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.5 5 7.5 10l5 5"/></svg>';
const ARROW_RIGHT_SVG = '<svg viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m7.5 5 5 5-5 5"/></svg>';

/**
 * Plain-DOM fields (tool panels): wrap `input` with the hover ‹ › step
 * arrows (styles in index.css, `.number-field`). Returns the wrapper to put
 * where the input was.
 */
export function attachNumberArrows(input: HTMLInputElement): HTMLElement {
  const wrapper = document.createElement('span');
  wrapper.className = 'number-field';
  const arrow = (direction: 1 | -1): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.tabIndex = -1;
    button.className = `number-field-arrow number-field-arrow-${direction < 0 ? 'left' : 'right'}`;
    button.innerHTML = direction < 0 ? ARROW_LEFT_SVG : ARROW_RIGHT_SVG;
    button.addEventListener('pointerdown', (e) => e.preventDefault());
    button.addEventListener('click', (e) => stepNumberInput(input, direction, { fine: e.shiftKey, coarse: e.ctrlKey || e.metaKey }));
    return button;
  };
  wrapper.append(arrow(-1), input, arrow(1));
  return wrapper;
}
